import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Stack,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  useTheme,
  Chip,
  Autocomplete,
  Snackbar,
  Alert
} from "@mui/material";
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Comment as CommentIcon,
  Delete as DeleteIcon,    
} from "@mui/icons-material";
import client from "../../../../API"; // axios instance with baseURL set to /api

const DialogShell = ({ title, icon, onClose, children }) => (
  <Dialog open onClose={onClose} fullWidth maxWidth="sm"
    PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}>
    <DialogTitle sx={{ px: 3, py: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        {icon}
        <Typography variant="h6" fontWeight={600}>{title}</Typography>
        <Box flexGrow={1} />
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Stack>
    </DialogTitle>
    <Divider />
    {children}
  </Dialog>
);

export default function ClientAddEditDialog({ popup, setPopup, pushClientToState }) {
  const theme = useTheme();
  const isAdd = popup.type === "add";
  const existing = popup.client || {};
  const [draft, setDraft] = useState({
    full_name:    existing.full_name    || "",
    company:      existing.company      || "",
    email:        existing.email        || "",
    location:     existing.location     || "",
    phone_number: existing.phone_number || "",
    profilephoto: existing.profilephoto || "",
    bio:          existing.bio          || "",
    tags:         existing.tags         || []
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAdd) {
      setDraft({
        full_name:    existing.full_name    || "",
        company:      existing.company      || "",
        email:        existing.email        || "",
        location:     existing.location     || "",
        phone_number: existing.phone_number || "",
        profilephoto: existing.profilephoto || "",
        bio:          existing.bio          || "",
        tags:         existing.tags         || []
      });
    }
  }, [popup.client]);

  const close = () => setPopup({ type: null });
  const onChange = e => {
    const { name, value } = e.target;
    setDraft(prev => ({ ...prev, [name]: value }));
  };
  const onTagsChange = (_, value) => {
    const splitTags = value.flatMap(tag => {
      if (typeof tag === 'string' && tag.includes(',')) {
        return tag.split(',').map(t => t.trim()).filter(Boolean);
      }
      return [tag];
    });
    setDraft(prev => ({ ...prev, tags: splitTags }));
  };

  const save = async () => {
    setError(null);
    const payload = {
      full_name:    draft.full_name,
      company:      draft.company,
      email:        draft.email,
      location:     draft.location,
      phone_number: draft.phone_number,
      bio:          draft.bio,
      profilephoto: draft.profilephoto,
      tags:         draft.tags,
    };

    try {
      let result;
      if (isAdd) {
        // use supervisor_add endpoint for create
        result = await client.users.createClient(payload);
      } else {
        const clientId = existing.client_id || existing.id;
        result = await client.users.updateClient(clientId, payload);
      }
      pushClientToState?.(result.data);
      close();
    } catch (e) {
      console.error("[ClientAddEditDialog] save error", e);
      const msg = e.response?.data?.error || e.message;
      setError(msg);
    }
  };
  const handleDelete = async () => {
        const id = existing.client_id ?? existing.id;
        if (!id) return;
    
        const ok = window.confirm(
          `Delete ${existing.full_name}?  This cannot be undone.`
        );
        if (!ok) return;
    
        try {
          await client.users.deleteClient(id);      // ← your new API helper
          // Tell the parent to strip the card
          pushClientToState?.({ deleted_id: id });
          close();
        } catch (err) {
          console.error("[ClientAddEditDialog] delete error", err);
          const msg = err.response?.data?.error || err.message;
          setError(msg);
        }
      };
  
  return (
    <>
      <DialogShell
        title={`${isAdd ? "Create New" : "Edit"} Client`}
        icon={<CommentIcon sx={{ color: "primary.main" }} />}
        onClose={close}
      >
        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={3}>
            {[
              ["full_name",   "Full Name"],
              ["company",     "Company"],
              ["email",       "Email"],
              ["phone_number","Phone"],
              ["location",    "Location"],
              ["profilephoto","Profile Photo URL"],
              ["bio",         "Bio", true]
            ].map(([key, label, multiline]) => (
              <TextField
                key={key}
                name={key}
                label={label}
                fullWidth
                size="small"
                multiline={!!multiline}
                minRows={multiline ? 3 : 1}
                value={draft[key] ?? ""}
                onChange={onChange}
                variant="outlined"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            ))}

            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={draft.tags}
              onChange={onTagsChange}
              renderTags={(value, getTagProps) =>
                value.map((opt, i) => (
                  <Chip
                    key={opt}
                    variant="outlined"
                    size="small"
                    label={opt}
                    {...getTagProps({ index: i })}
                  />
                ))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Tags"
                  placeholder="Add tag…"
                  size="small"
                  variant="outlined"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              )}
            />
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          {/* Delete appears only when editing */}
          {!isAdd && (
            <Button
             color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
             sx={{ mr: "auto" }}           /* pushes the others right */
            >
              Delete
            </Button>
          )}

          <Button onClick={close}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={
              !draft.full_name.trim() ||
              !draft.company.trim() ||
              !draft.email.trim()
            }
            sx={{
              px: 4,
              borderRadius: 2,
              boxShadow: theme.shadows[1],
              "&:hover": { boxShadow: theme.shadows[3] }
            }}
            onClick={save}
          >
            {isAdd ? "Create Client" : "Save Changes"}
          </Button>
        </DialogActions>
      </DialogShell>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}
